<template>
    <div class="input-group" :class="[`input-group-${status}`, tip ? 'has-tip' : '']">
        <label class="form-label" v-if="label" :for="inputId">{{ label }}</label>
        <Field
            :id="inputId"
            v-model="value"
            v-maska
            @focus="handleFocus"
            @blur="handleBlur"
            :data-maska="maska"
            :data-maska-tokens="maskaTokens"
            class="form-control"
            :type="type"
            :name="name"
            :class="[`input-${tipType}`, toBlur ? 'to-blur' : '', (toBlur && isBlurred) ? 'blurred' : '']"
            :placeholder="placeholder"
            :rules="validate"
            :max="max"
            :maxlength="maxlength"
            :autocomplete="autocomplete"
            :disabled="disabled"
            :aria-invalid="tipType === 'danger' ? 'true' : undefined"
            :aria-describedby="tip ? tipId : undefined"
        />
        <ul class="input-datalist" v-if="_list.length" v-show="showDatalist">
            <li class="datalist-item" v-for="opt in _list" :key="opt.text" @click.stop="handleDatalistItemClick(opt)">
                {{ opt.text }}
            </li>
        </ul>
        <div
            :id="tipId"
            class="tip"
            :class="`text-${validateClass ? validateClass : 'muted'}`"
        >
            <small class="tip-text">
                <ErrorMessage :name="name" />
                {{ tip }}
            </small>
            <div class="tip-icon" v-if="tipType == 'danger'">
                <i class="bi bi-exclamation-triangle-fill text-danger"></i>
            </div>
        </div>
    </div>
</template>

<script>

import { Field, ErrorMessage } from 'vee-validate'
import { vMaska } from "maska"

export default {
    name: 'Input',
    data: () => ({
        isFocused: false,
        validateClass: 'regular',
        newDisabled: null,
        showDatalist: false,
    }),
    directives: { maska: vMaska },
    props: {
        modelValue: [String, Number],
        type: {
            type: String,
            default: 'text'
        },
        name: String,
        autocomplete: {
            type: String,
            default: 'on'
        },
        filterList: {
            type: Boolean,
            default: true
        },
        disabled: {
            type: Boolean,
            default: false
        },
        maska: {
            type: String,
            default: ''
        },
        maskaTokens: {
            type: String,
            default: ''
        },
        label: String,
        tip: String,
        tipType: {
            type: String,
            default: 'regular'
        },
        status: {
            type: String,
            default: 'regular'
        },
        placeholder: String,
        min: {
            type: [String, Number],
            default: 1
        },
        max: {
            type: [String, Number],
            default: ''
        },
        maxlength: {
            type: [String, Number],
            default: ''
        },
        list: {
            type: Array,
            default: []
        },
        validations: {
            type: Array,
            default: []
        },
        toBlur: {
            type: Boolean,
            default: false
        }
    },
    components: {
        Field,
        ErrorMessage
    },
    emits: ['update:modelValue', 'change', 'datalistClicked', 'focused', 'blurred'],
    computed: {
        inputId() {
            return `input-${this.name || 'unnamed'}`
        },
        tipId() {
            return `${this.inputId}-help`
        },
        disabled: {
            get() {
                if(this.newDisabled !== null) return this.newDisabled
                return this.disabled
            }
        },
        _list: {
            get() {
                if(this.list && (!this.value || this.value.length == 0) && !this.filterList) return this.list
                return this.filteredList(this.list)
            }
        },
        value: {
            get() {
                return this.modelValue
            },
            set(value) {
                this.$emit('change', value, this.name)
                this.$emit('update:modelValue', value)
            }
        },
        isBlurred: {
            get() {
                return this.$store.getters.getBlur == true
            },
            set(value) {
                this.$store.commit('setBlur', value)
            },
        },
    },
    watch: {
        value() {
            this.onChange()
        }
    },
    mounted() {
        window.addEventListener('storage', this.syncBlurState)
    },
    beforeDestroy() {
        window.removeEventListener('storage', this.syncBlurState)
    },
    methods: {
        syncBlurState(event) {
            if (event.key === 'isBlurred') {
                this.isBlurred = event.newValue === 'true';
            }
        },
        disable(status = true) {
            this.newDisabled = status
        },
        hasFocus() {
            return this.isFocused
        },
        handleFocus() {
            this.isFocused = true
            this.showDatalist = true
            this.$emit('focused')
        },
        handleBlur() {
            this.isFocused = false
            if(this.showDatalist) {
                setTimeout(() => {
                    if(!this.isFocused)
                        this.showDatalist = false
                    this.$emit('blurred')
                }, 500)
            }
        },
        onChange(e) {
            this.$emit('change', e)
        },
        handleDatalistItemClick(item) {
            this.modalValue = item.text
            this.$emit('datalistClicked', item)
            this.showDatalist = false
        },
        filteredList(arr) {
            return this.filterList ? arr.filter(item => item.text.toLowerCase().includes(this.value.toLowerCase())) : arr
        },
        validate(value = '') {
            if(this.disabled || this.showDatalist) return true

            if(typeof value === 'string' || value instanceof String) value = value.trim()

            let types = {
                date: () => {
                    if(document.querySelector(`#input-${this.name}`)?.validity.badInput) {
                        return this.$t('form.validate.date-invalid')
                    }

                    if(!value) return true

                    const regex = /^[12]\d{3}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/
                    if(!regex.test(value)) return this.$t('form.validate.date')

                    // Check if the date is valid
                    const inputDate = new Date(value)
                    const [year, month, day] = value.split('-').map(Number)

                    // Ensure the parsed date matches the input (handles cases like 30th Feb)
                    if(
                        inputDate.getFullYear() !== year ||
                        inputDate.getMonth() + 1 !== month || // Month is zero-based
                        inputDate.getDate() !== day
                    ) {
                        return this.$t('form.validate.date-invalid') // Add a key for invalid dates
                    }

                    return true
                },
                dateConflict: () => {
                    if(!value) return true

                    return this.$t('form.validate.date-conflict')
                },
                dateCompare: () => {
                    if(!value) return true

                    let currentDate = new Date()
                    let inputDate = new Date(value)

                    if(inputDate > currentDate) return this.$t('form.validate.date-future')

                    return true
                },
                dateRecent: () => {
                    if(!value) return true

                    let referenceDate = new Date('1980-01-01')
                    let inputDate = new Date(value)

                    if(inputDate < referenceDate) return this.$t('form.validate.date-too-old')

                    return true
                },
                email: () => {
                    if(!value) return true

                    const regex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i
                    if(!regex.test(value)) return this.$t('form.validate.email')
                    
                    return true
                },
                min: (val) => {
                    if(value <= val) return this.$t('form.validate.min', { val: val })
                    
                    return true
                },
                max: (val) => {
                    if(!value) return true

                    if(value >= val) return this.$t('form.validate.max', { val: val })
                    
                    return true
                },
                minLength: (len) => {
                    if(value.length < len) return this.$t('form.validate.min-length', { len: len })
                    
                    return true
                },
                maxLength: (len) => {
                    if(!value) return true

                    if(value.length > len) return this.$t('form.validate.max-length', { len: len })
                    
                    return true
                },
                confirm: () => {

                },
                required: () => {
                    if(!value) return this.$t('form.validate.required-field')

                    return true
                },
                test: () => {
                    if(process.env.VUE_APP_DEBUG) console.log('test validate', value)

                    return true
                }
            }

            let r = true

            if(this.validations.length > 0) {
                this.validateClass = 'danger'

                for(let i in this.validations) {
                    let v = this.validations[i],
                        regexp = /\(([^)]+)\)/,
                        matches = regexp.exec(v),
                        arg = matches ? matches[1] : null
                    
                    if(arg) v = v.replace(matches[0], '')

                    r = types[v](arg)

                    if(r !== true) return r
                }

                this.validateClass = 'regular'
            }

            return r
        }
    }
}

</script>

<style scoped>

.input-group {
    flex-wrap: wrap;
    margin-bottom: 1rem;
}

.input-group input.form-control:focus {
    border-color: var(--kdp-primary-accent);
    outline: 0;
    box-shadow: inset 0 0 0 1px var(--kdp-primary-accent);
}

.form-label {
    flex: 0 0 100%;
}

input.form-control {
    padding: 0 auto;
    border-radius: var(--kdp-radius-sm) !important;
}

input::placeholder {
    color: var(--kdp-neutral-text);
}

.input-danger {
    border-color: var(--kdp-status-danger);
}

.input-warning {
    border-color: var(--kdp-status-warning);
}

.input-success {
    border-color: var(--kdp-status-success);
}

.input-group .input-datalist {
    position: absolute;
    width: 100%;
    max-height: 120px;
    top: 100%;
    padding: 0;
    background-color: var(--kdp-white);
    border: 1px solid var(--kdp-border-light);
    border-radius: 0 0 0.5em 0.5em !important;
    list-style: none;
    overflow-y: auto;
    z-index: 1;
}

.datalist-item {
    color: var(--kdp-black);
    padding: .25em .5em;
    cursor: pointer;
}

.datalist-item:hover {
    background-color: var(--kdp-border-light);
}

.tip {
    display: flex;
    width: 100%;
    justify-content: space-between;
    margin: .125rem 0;
    font-size: .75rem;
}

</style>
